/* eslint-disable import/no-cycle */
import * as React from 'react';
import { isCtrl } from '../../utils/BrowserDetection';
import {
  getDateWithDayOffset,
  getDateWithYearOffset,
  getDateWithMonthOffset,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getFirstDayOfWeek,
  getLastDayOfWeek,
  getFirstDisplayedDate,
  getStartDayOfWeek
} from '../../utils/Date';
import { findBlurRelatedTarget } from '../../utils/DomUtil';
import { NavigationKey } from '../../utils/ReactHelpers';
import { DatePickerRow, DatePickerRowProps } from './DatePickerRow';
import { datePickerBodyStyle } from './DatePickerStyles';

export interface DatePickerBodyProps {
  focusedDate: Date;
  isDateDisabled?: (date: Date) => boolean;
  onChange: (date: Date) => void;
  onConfirm: (date: Date) => void;
  onFocusedDateChange: (date: Date) => void;
  selectedDate?: Date;
  showWeekNumbers: boolean;
}

export interface DatePickerBodyState {
  hasFocus: boolean;
}

export class DatePickerBody extends React.PureComponent<DatePickerBodyProps, DatePickerBodyState> {
  private element: HTMLElement | null;

  public state: DatePickerBodyState = {
    hasFocus: false,
  };

  private handleRef = (ref: HTMLElement | null): void => {
    this.element = ref;
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const startDayOfWeek = getStartDayOfWeek(this.props.showWeekNumbers);
    const key = e.key;
    if (isCtrl(e)) {
      switch (key) {
        case 'Home':
          this.handleFocusedDateChange(getFirstDayOfMonth(this.props.focusedDate));
          break;
        case 'End':
          this.handleFocusedDateChange(getLastDayOfMonth(this.props.focusedDate));
          break;
        default:
          return;
      }
    } else if (e.altKey) {
      switch (key) {
        case 'PageDown':
          this.handleFocusedDateChange(getDateWithYearOffset(this.props.focusedDate, 1));
          break;
        case 'PageUp':
          this.handleFocusedDateChange(getDateWithYearOffset(this.props.focusedDate, -1));
          break;
        default:
          return;
      }
    } else {
      switch (key) {
        case 'ArrowRight':
          this.handleFocusedDateChange(getDateWithDayOffset(this.props.focusedDate, 1));
          break;
        case 'ArrowLeft':
          this.handleFocusedDateChange(getDateWithDayOffset(this.props.focusedDate, -1));
          break;
        case 'ArrowUp':
          this.handleFocusedDateChange(getDateWithDayOffset(this.props.focusedDate, -7));
          break;
        case 'ArrowDown':
          this.handleFocusedDateChange(getDateWithDayOffset(this.props.focusedDate, 7));
          break;
        case 'PageDown':
          this.handleFocusedDateChange(getDateWithMonthOffset(this.props.focusedDate, 1));
          break;
        case 'PageUp':
          this.handleFocusedDateChange(getDateWithMonthOffset(this.props.focusedDate, -1));
          break;
        case 'Home':
          this.handleFocusedDateChange(getFirstDayOfWeek(this.props.focusedDate, startDayOfWeek));
          break;
        case 'End':
          this.handleFocusedDateChange(getLastDayOfWeek(this.props.focusedDate, startDayOfWeek));
          break;
        case ' ':
          this.keyUpHandlers[key] = () => {
            if (!this.props.isDateDisabled?.(this.props.focusedDate)) {
              this.props.onChange(this.props.focusedDate);
            }
          };
          break;
        case 'Enter':
          this.keyUpHandlers[key] = () => {
            if (!this.props.isDateDisabled?.(this.props.focusedDate)) {
              this.props.onConfirm(this.props.focusedDate);
            }
          };
          break;
        default:
          return;
      }
    }
    e.preventDefault();
    e.stopPropagation();
  }

  private readonly keyUpHandlers: { [key in NavigationKey]?: React.KeyboardEventHandler<HTMLElement> } = {};
  private handleKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    const key = e.key as NavigationKey;
    if (this.keyUpHandlers[key]) {
      this.keyUpHandlers[key]!(e);
      delete this.keyUpHandlers[key];
      e.stopPropagation();
      e.preventDefault();
    }
  }

  private handleFocusedDateChange = (date: Date): void => {
    if (!this.state.hasFocus) {
      this.setState({ hasFocus: true });
    }
    this.props.onFocusedDateChange(date);
  }

  private handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    const relatedTarget = findBlurRelatedTarget(e);
    if (this.element && !this.element.contains(relatedTarget)) {
      if (this.state.hasFocus) {
        this.setState({ hasFocus: false });
      }
    }
  }

  public render(): JSX.Element {
    const startDayOfWeek = getStartDayOfWeek(this.props.showWeekNumbers);
    const currentPageStartDate = getFirstDisplayedDate(this.props.focusedDate, startDayOfWeek);
    const rows: JSX.Element[] = [];
    const rowNum = 6;
    const daysOfWeekNum = 7;
    for (let i = 0; i < rowNum; i++) {
      const rowProps: DatePickerRowProps = {
        bodyHasFocus: this.state.hasFocus,
        focusedDate: this.props.focusedDate,
        isDateDisabled: this.props.isDateDisabled,
        onChange: this.props.onChange,
        onFocusedDateChange: this.handleFocusedDateChange,
        selectedDate: this.props.selectedDate,
        showWeekNumbers: this.props.showWeekNumbers,
        startDate: getDateWithDayOffset(currentPageStartDate, i * daysOfWeekNum),
      };
      rows.push(<DatePickerRow key={i} {...rowProps} />);
    }
    return (
      <div
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        ref={this.handleRef}
        style={datePickerBodyStyle}
      >
        {rows}
      </div>
    );
  }
}
